import { useState, useEffect, useCallback, useRef } from 'react'
import { useForm, useWatch, Controller } from 'react-hook-form'
import { useFormData, useFormActions } from '@hooks/useFormStore'
import { useOperatorAuth } from '@hooks/useOperatorAuth'
import { Button } from '@components/UI/Button'
import { Input } from '@components/UI/Input'
import { Modal } from '@components/UI/Modal'
import { ComuneAutocomplete } from '@components/UI/ComuneAutocomplete'
import { codiceFiscaleApi, coperturaApi, clearIdentityDocumentsServerState } from '@/services/api'
import { hasFormatoCodiceFiscaleValido, isCodiceFiscaleValido } from '@utils/codiceFiscale'
import type { FormData, CoperturaComune } from '@/types'
import styles from './FormSection.module.css'

type AnagraficaFields = Pick<FormData, 
  'nome' | 'cognome' | 'codice_fiscale' | 'data_nascita' | 'luogo_nascita' | 'provincia_nascita' | 'telefono' | 'email' | 'pec' |
  'indirizzo_residenza' | 'cap_residenza' | 'citta_residenza' | 'provincia_residenza' |
  'stesso_indirizzo' | 'indirizzo_fornitura' | 'cap_fornitura' | 'citta_fornitura' | 'provincia_fornitura' |
  'ragione_sociale' | 'partita_iva' | 'codice_fiscale_azienda' | 'codice_sdi' | 'pec_azienda' |
  'sede_legale_indirizzo' | 'sede_legale_cap' | 'sede_legale_citta' | 'sede_legale_provincia' |
  'rappresentante_legale_nome' | 'rappresentante_legale_cognome' | 'rappresentante_legale_cf' |
  'referente_azienda_nome' | 'referente_azienda_telefono' |
  'grande_impresa' | 'agevolazione_iva' | 'agevolazione_accise'
>

/**
 * Converte GG/MM/AAAA o AAAA-MM-GG in Date, restituendo null se la data non
 * esiste sul calendario. Il controllo di rollover e' indispensabile: `new Date`
 * non segnala il 39 giugno, lo sposta silenziosamente al 9 luglio.
 */
function parseDataIta(dateStr: string): Date | null {
  if (!dateStr) return null
  
  let giorno: number, mese: number, anno: number
  
  // Supporta formato GG/MM/AAAA o AAAA-MM-GG
  if (dateStr.includes('/')) {
    const parti = dateStr.split('/')
    if (parti.length !== 3) return null
    giorno = parseInt(parti[0], 10)
    mese = parseInt(parti[1], 10) - 1 // Mesi 0-indexed
    anno = parseInt(parti[2], 10)
  } else if (dateStr.includes('-')) {
    const parti = dateStr.split('-')
    if (parti.length !== 3) return null
    anno = parseInt(parti[0], 10)
    mese = parseInt(parti[1], 10) - 1
    giorno = parseInt(parti[2], 10)
  } else {
    return null
  }
  
  if (isNaN(giorno) || isNaN(mese) || isNaN(anno)) return null
  
  const data = new Date(anno, mese, giorno)
  if (data.getFullYear() !== anno || data.getMonth() !== mese || data.getDate() !== giorno) {
    return null
  }
  
  return data
}

// Funzione per calcolare l'età dalla data di nascita
function calcolaEta(dataNascita: string): number | null {
  const nascita = parseDataIta(dataNascita)
  if (!nascita) return null
  
  const oggi = new Date()
  
  let eta = oggi.getFullYear() - nascita.getFullYear()
  const meseDiff = oggi.getMonth() - nascita.getMonth()
  
  // Se il compleanno non e ancora passato quest'anno, sottrai 1
  if (meseDiff < 0 || (meseDiff === 0 && oggi.getDate() < nascita.getDate())) {
    eta--
  }
  
  return eta
}

/** Messaggio non bloccante se il comune scelto non ha copertura coerente con il tipo fornitura. */
function getMessaggioCoperturaComune(
  c: CoperturaComune,
  tipoFornitura: FormData['tipo_fornitura'],
): string | null {
  const needLuce = tipoFornitura === 'luce' || tipoFornitura === 'entrambi'
  const needGas = tipoFornitura === 'gas' || tipoFornitura === 'entrambi'

  if (!c.luce_disponibile && !c.gas_disponibile) {
    return 'Per il comune selezionato non risulta possibile l\'attivazione dei servizi Heracomm (né luce né gas). In fase di invio la pratica sarà comunque verificata; valuta se l\'indirizzo o il tipo di fornitura sono corretti.'
  }

  const mancanze: string[] = []
  if (needLuce && !c.luce_disponibile) mancanze.push('la fornitura elettrica')
  if (needGas && !c.gas_disponibile) mancanze.push('la fornitura gas')

  if (mancanze.length === 0) return null

  const cosa = mancanze.length === 2 ? `${mancanze[0]} e ${mancanze[1]}` : mancanze[0]
  return `Attenzione: per il comune selezionato non risulta disponibile ${cosa}. Verifica tipo di fornitura (passo successivo) o indirizzo di attivazione prima di proseguire.`
}

export function AnagraficaSection() {
  const formData = useFormData()
  const { setData, nextStep, prevStep, resetForm, setCoperturaFornitura } = useFormActions()
  const { operator } = useOperatorAuth()
  // Blacklist regioni dell'operatore (es. Adventure: CAMPANIA/SICILIA/CALABRIA
  // NON vendibili). Vuota = nessuna limitazione territoriale (copertura standard).
  const blockedRegions = operator?.blocked_regions ?? []
  const [showZonaNonCoperta, setShowZonaNonCoperta] = useState(false)
  const [isDecodingCF, setIsDecodingCF] = useState(false)
  const [cfDecodingError, setCfDecodingError] = useState<string | null>(null)
  // Bypass validazione codice fiscale (solo domestico): se l'operatore conferma
  // di voler procedere, il pattern del CF non blocca piu' il submit e l'errore
  // viene mostrato come avviso non bloccante. Il bypass si auto-disattiva non
  // appena l'operatore modifica nuovamente il CF, cosi' i dati corretti
  // tornano a essere validati normalmente.
  const [cfBypass, setCfBypass] = useState(false)
  const [isCheckingCopertura, setIsCheckingCopertura] = useState(false)
  const [coperturaMessage, setCoperturaMessage] = useState<string | null>(null)
  const [coperturaWarningResidenza, setCoperturaWarningResidenza] = useState<string | null>(null)
  const [coperturaWarningFornitura, setCoperturaWarningFornitura] = useState<string | null>(null)
  const [coperturaWarningSedeLegale, setCoperturaWarningSedeLegale] = useState<string | null>(null)

  const isBusiness = formData.tipo_cliente === 'business'

  const handleRicomincia = () => {
    setShowZonaNonCoperta(false)
    setCoperturaMessage(null)
    resetForm()
    void clearIdentityDocumentsServerState()
  }
  
  const {
    register,
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm<AnagraficaFields>({
    mode: 'onBlur',
    defaultValues: {
      nome: formData.nome,
      cognome: formData.cognome,
      codice_fiscale: formData.codice_fiscale,
      data_nascita: formData.data_nascita,
      luogo_nascita: formData.luogo_nascita,
      provincia_nascita: formData.provincia_nascita,
      telefono: formData.telefono,
      email: formData.email,
      pec: formData.pec,
      indirizzo_residenza: formData.indirizzo_residenza,
      cap_residenza: formData.cap_residenza,
      citta_residenza: formData.citta_residenza,
      provincia_residenza: formData.provincia_residenza,
      stesso_indirizzo: formData.stesso_indirizzo ?? true,
      indirizzo_fornitura: formData.indirizzo_fornitura,
      cap_fornitura: formData.cap_fornitura,
      citta_fornitura: formData.citta_fornitura,
      provincia_fornitura: formData.provincia_fornitura,
      // Business fields
      ragione_sociale: formData.ragione_sociale,
      partita_iva: formData.partita_iva,
      codice_fiscale_azienda: formData.codice_fiscale_azienda,
      codice_sdi: formData.codice_sdi,
      pec_azienda: formData.pec_azienda,
      sede_legale_indirizzo: formData.sede_legale_indirizzo,
      sede_legale_cap: formData.sede_legale_cap,
      sede_legale_citta: formData.sede_legale_citta,
      sede_legale_provincia: formData.sede_legale_provincia,
      rappresentante_legale_nome: formData.rappresentante_legale_nome,
      rappresentante_legale_cognome: formData.rappresentante_legale_cognome,
      rappresentante_legale_cf: formData.rappresentante_legale_cf,
      grande_impresa: formData.grande_impresa,
      agevolazione_iva: formData.agevolazione_iva,
      agevolazione_accise: formData.agevolazione_accise,
    }
  })

  // Watch stesso_indirizzo to show/hide fornitura fields
  const stessoIndirizzo = useWatch({ control, name: 'stesso_indirizzo' })

  // Tracker "comune confermato dalla tabella heracomm_comuni": l'operatore
  // DEVE selezionare la voce dal menu autocomplete; il free-typing non e'
  // ammesso. Usiamo dei ref piuttosto che useState per non far ri-renderare
  // tutta la sezione ad ogni keystroke.
  const cittaResidenzaConfirmedRef = useRef<boolean>(!!formData.citta_residenza)
  const cittaFornituraConfirmedRef = useRef<boolean>(!!formData.citta_fornitura)
  const sedeLegaleCittaConfirmedRef = useRef<boolean>(!!formData.sede_legale_citta)
  const ultimoComuneResidenzaRef = useRef<CoperturaComune | null>(null)
  const ultimoComuneFornituraRef = useRef<CoperturaComune | null>(null)
  const ultimoComuneSedeLegaleRef = useRef<CoperturaComune | null>(null)

  const handleSelezioneComuneResidenza = useCallback(
    (c: CoperturaComune) => {
      setValue('citta_residenza', c.comune, { shouldValidate: true })
      if (c.cap) setValue('cap_residenza', c.cap, { shouldValidate: true })
      if (c.provincia) setValue('provincia_residenza', c.provincia, { shouldValidate: true })
      cittaResidenzaConfirmedRef.current = true
      clearErrors('citta_residenza')
      ultimoComuneResidenzaRef.current = c
      // Warning copertura applicabile solo se questo indirizzo coincide con la fornitura,
      // ovvero: cliente domestico con stesso_indirizzo = true. Per business la residenza
      // del rappresentante legale non e' mai l'indirizzo di fornitura.
      if (!isBusiness && stessoIndirizzo) {
        setCoperturaWarningResidenza(getMessaggioCoperturaComune(c, formData.tipo_fornitura))
      } else {
        setCoperturaWarningResidenza(null)
      }
    },
    [setValue, clearErrors, stessoIndirizzo, formData.tipo_fornitura, isBusiness],
  )

  const handleSelezioneComuneFornitura = useCallback(
    (c: CoperturaComune) => {
      setValue('citta_fornitura', c.comune, { shouldValidate: true })
      if (c.cap) setValue('cap_fornitura', c.cap, { shouldValidate: true })
      if (c.provincia) setValue('provincia_fornitura', c.provincia, { shouldValidate: true })
      cittaFornituraConfirmedRef.current = true
      clearErrors('citta_fornitura')
      ultimoComuneFornituraRef.current = c
      setCoperturaWarningFornitura(getMessaggioCoperturaComune(c, formData.tipo_fornitura))
    },
    [setValue, clearErrors, formData.tipo_fornitura],
  )

  const handleSelezioneComuneSedeLegale = useCallback(
    (c: CoperturaComune) => {
      setValue('sede_legale_citta', c.comune, { shouldValidate: true })
      if (c.cap) setValue('sede_legale_cap', c.cap, { shouldValidate: true })
      if (c.provincia) setValue('sede_legale_provincia', c.provincia, { shouldValidate: true })
      sedeLegaleCittaConfirmedRef.current = true
      clearErrors('sede_legale_citta')
      ultimoComuneSedeLegaleRef.current = c
      // Se la fornitura coincide con la sede legale (stesso_indirizzo), avvisiamo
      // di un eventuale gap di copertura. Altrimenti la verifica avverra' sul comune
      // di fornitura: niente warning su sede legale.
      if (stessoIndirizzo) {
        setCoperturaWarningSedeLegale(getMessaggioCoperturaComune(c, formData.tipo_fornitura))
      } else {
        setCoperturaWarningSedeLegale(null)
      }
    },
    [setValue, clearErrors, stessoIndirizzo, formData.tipo_fornitura],
  )

  useEffect(() => {
    // Residenza (domestico): warning solo se stesso_indirizzo (residenza == fornitura).
    if (!isBusiness && stessoIndirizzo && ultimoComuneResidenzaRef.current) {
      setCoperturaWarningResidenza(
        getMessaggioCoperturaComune(ultimoComuneResidenzaRef.current, formData.tipo_fornitura),
      )
    } else {
      setCoperturaWarningResidenza(null)
    }
    // Sede legale (business): warning solo se stesso_indirizzo (sede legale == fornitura).
    if (isBusiness && stessoIndirizzo && ultimoComuneSedeLegaleRef.current) {
      setCoperturaWarningSedeLegale(
        getMessaggioCoperturaComune(ultimoComuneSedeLegaleRef.current, formData.tipo_fornitura),
      )
    } else {
      setCoperturaWarningSedeLegale(null)
    }
    // Fornitura: warning solo se NON stesso_indirizzo (campi separati).
    if (!stessoIndirizzo && ultimoComuneFornituraRef.current) {
      setCoperturaWarningFornitura(
        getMessaggioCoperturaComune(ultimoComuneFornituraRef.current, formData.tipo_fornitura),
      )
    } else {
      setCoperturaWarningFornitura(null)
    }
  }, [stessoIndirizzo, formData.tipo_fornitura, isBusiness])
  
  // Watch codice_fiscale per decodifica automatica
  const codiceFiscale = useWatch({ control, name: 'codice_fiscale' })
  // Per il business la decodifica deve agganciarsi al CF del rappresentante
  // legale: e' lui il titolare fisico i cui dati anagrafici (data/luogo/prov.
  // nascita) compaiono nella sezione "Dati Anagrafici Rappresentante Legale"
  // e finiscono sulla dichiarazione sostitutiva e sui moduli SEPA.
  const rappresentanteLegaleCf = useWatch({ control, name: 'rappresentante_legale_cf' })

  // Watch data_nascita per calcolo età
  const dataNascita = useWatch({ control, name: 'data_nascita' })
  
  // Calcola età e verifica se over 80 o minorenne
  const eta = calcolaEta(dataNascita || '')
  const isOver80 = eta !== null && eta >= 80
  const isMinorenne = eta !== null && eta < 18
  
  // Decodifica codice fiscale ed estrai data/luogo nascita
  const decodificaCodiceFiscale = useCallback(async (cf: string) => {
    // Valida formato CF prima di chiamare API
    if (!cf || !hasFormatoCodiceFiscaleValido(cf)) return
    
    setIsDecodingCF(true)
    setCfDecodingError(null)
    try {
      const esito = await codiceFiscaleApi.decodifica(cf.toUpperCase())
      const risultato = esito.data
      
      if (risultato) {
        // Popola la data solo se esiste davvero sul calendario: un CF con un
        // refuso puo' decodificare in date impossibili (es. 39/06/2002) che
        // supererebbero i controlli di forma e romperebbero il salvataggio.
        if (risultato.data_nascita_formato_ita && parseDataIta(risultato.data_nascita_formato_ita)) {
          setValue('data_nascita', risultato.data_nascita_formato_ita, { shouldValidate: true })
        }
        
        // Popola luogo di nascita se trovato
        if (risultato.luogo_nascita) {
          setValue('luogo_nascita', risultato.luogo_nascita, { shouldValidate: true })
        }
        
        // Popola provincia di nascita se trovata
        if (risultato.provincia_nascita) {
          setValue('provincia_nascita', risultato.provincia_nascita, { shouldValidate: true })
        }
        
        // Verifica se i campi sono stati popolati
        if (!risultato.luogo_nascita || !risultato.provincia_nascita) {
          setCfDecodingError('Comune non trovato. Inserisci manualmente luogo e provincia di nascita.')
        }
      } else {
        setCfDecodingError(
          esito.message ?? 'Impossibile decodificare il codice fiscale. Inserisci manualmente i dati.'
        )
      }
    } catch (err) {
      console.error('Errore decodifica CF:', err)
      setCfDecodingError('Errore nella decodifica. Inserisci manualmente i dati di nascita.')
    } finally {
      setIsDecodingCF(false)
    }
  }, [setValue])
  
  // Effetto per decodificare CF quando cambia (domestico = CF intestatario,
  // business = CF rappresentante legale). Solo uno dei due e' effettivamente
  // editabile in pagina perche' i campi "domestici" sono nascosti per business
  // e viceversa, quindi non c'e' conflitto fra le due dipendenze.
  useEffect(() => {
    if (!isBusiness && codiceFiscale && codiceFiscale.length === 16) {
      decodificaCodiceFiscale(codiceFiscale)
    }
  }, [isBusiness, codiceFiscale, decodificaCodiceFiscale])

  useEffect(() => {
    if (isBusiness && rappresentanteLegaleCf && rappresentanteLegaleCf.length === 16) {
      decodificaCodiceFiscale(rappresentanteLegaleCf)
    }
  }, [isBusiness, rappresentanteLegaleCf, decodificaCodiceFiscale])

  const onSubmit = async (data: AnagraficaFields) => {
    // Validazione "comune confermato": l'operatore DEVE aver selezionato il
    // comune dal menu autocomplete della tabella heracomm_comuni. Se ha solo
    // digitato free-text che potrebbe non esistere nella tabella, blocchiamo.
    let blockingError = false
    if (data.citta_residenza && !cittaResidenzaConfirmedRef.current) {
      setError('citta_residenza', {
        type: 'manual',
        message: 'Seleziona il comune dal menu (deve provenire dalla tabella ufficiale)',
      })
      blockingError = true
    }
    // Sede legale (solo business): stesso vincolo del menu autocomplete.
    if (isBusiness && data.sede_legale_citta && !sedeLegaleCittaConfirmedRef.current) {
      setError('sede_legale_citta', {
        type: 'manual',
        message: 'Seleziona il comune dal menu (deve provenire dalla tabella ufficiale)',
      })
      blockingError = true
    }
    if (!data.stesso_indirizzo && data.citta_fornitura && !cittaFornituraConfirmedRef.current) {
      setError('citta_fornitura', {
        type: 'manual',
        message: 'Seleziona il comune dal menu (deve provenire dalla tabella ufficiale)',
      })
      blockingError = true
    }
    if (blockingError) return

    // Se "stesso indirizzo" e' attivo: per business copiamo dalla sede legale,
    // per domestico dall'indirizzo di residenza. La copia popola sempre i
    // campi *_fornitura cosi' la successiva verifica copertura ha sempre dati.
    if (data.stesso_indirizzo) {
      if (isBusiness) {
        data.indirizzo_fornitura = data.sede_legale_indirizzo
        data.cap_fornitura = data.sede_legale_cap
        data.citta_fornitura = data.sede_legale_citta
        data.provincia_fornitura = data.sede_legale_provincia
      } else {
        data.indirizzo_fornitura = data.indirizzo_residenza
        data.cap_fornitura = data.cap_residenza
        data.citta_fornitura = data.citta_residenza
        data.provincia_fornitura = data.provincia_residenza
      }
    }

    // Source-of-truth unica: tabella heracomm_comuni via API.
    // Una vecchia whitelist statica di province bloccava luogo coperti
    // solo a luce (es. Cagliari/Monserrato): rimossa, lasciamo decidere all'API.
    setIsCheckingCopertura(true)
    setCoperturaMessage(null)

    try {
      const copertura = await coperturaApi.verifica(
        data.citta_fornitura,
        data.provincia_fornitura,
        data.cap_fornitura
      )

      // Deroga copertura per operatore (es. Adventure): nessuna limitazione
      // territoriale, puo' vendere ovunque. L'unica eccezione sono le regioni
      // in blocked_regions, gestite dal controllo qui sotto.
      const coverageUnrestricted = !!operator?.coverage_unrestricted

      // Blocco territoriale per operatore (es. Adventure): alcune regioni non
      // sono vendibili. Controllo anticipato cosi' un comune in regione bloccata
      // riceve un messaggio chiaro. Blacklist vuota = nessuna limitazione.
      if (blockedRegions.length > 0) {
        const regioneFornitura = (copertura.regione ?? '').toUpperCase().trim()
        if (regioneFornitura && blockedRegions.includes(regioneFornitura)) {
          setCoperturaFornitura(null)
          setCoperturaMessage(
            `Il servizio non è disponibile nelle seguenti regioni: ${blockedRegions.join(', ')}. ` +
            `Seleziona un comune di un'altra regione.`
          )
          setShowZonaNonCoperta(true)
          setIsCheckingCopertura(false)
          return
        }
      }

      // Blocca solo se nessun servizio (ne luce ne gas) e' attivabile.
      // Coperture parziali (solo luce o solo gas) sono ammesse: lo step
      // Fornitura disabilita automaticamente l'opzione non disponibile.
      if (!coverageUnrestricted && (!copertura.trovato || (!copertura.luce_disponibile && !copertura.gas_disponibile))) {
        setCoperturaFornitura(null)
        setCoperturaMessage(copertura.messaggio)
        setShowZonaNonCoperta(true)
        setIsCheckingCopertura(false)
        return
      }

      const companyResolved =
        copertura.company === 'estenergy' ? 'estenergy' : 'heracomm'

      // Salva la copertura nello store (anche company/societa_ee per il passo Fornitura)
      setCoperturaFornitura({
        verificato: true,
        comune: copertura.comune,
        provincia: copertura.provincia || data.provincia_fornitura,
        luce_disponibile: copertura.luce_disponibile || coverageUnrestricted,
        gas_disponibile: copertura.gas_disponibile || coverageUnrestricted,
        societa_ee: copertura.societa_ee ?? null,
        company: companyResolved,
      })

      setData({
        ...data,
        company: companyResolved,
      })
      nextStep()
    } catch (error) {
      // In caso di errore API, procedi comunque (fallback). Il blocco regionale
      // e' una regola business "soft": non ha senso bloccare tutte le vendite
      // su un errore temporaneo dell'API di copertura.
      console.warn('Errore verifica copertura:', error)
      setCoperturaFornitura({
        verificato: false,
        comune: data.citta_fornitura,
        provincia: data.provincia_fornitura,
        luce_disponibile: true,
        gas_disponibile: true,
        societa_ee: null,
        company: 'heracomm',
      })
      setData({ ...data, company: 'heracomm' })
      nextStep()
    } finally {
      setIsCheckingCopertura(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionIcon}>
          <i className={isBusiness ? 'bi bi-building-fill' : 'bi bi-person-fill'} />
        </div>
        <div>
          <h2 className={styles.sectionTitle}>
            {isBusiness ? 'Dati Azienda e Rappresentante' : 'Dati Anagrafici'}
          </h2>
          <p className={styles.sectionDescription}>
            {isBusiness
              ? 'Inserisci i dati dell\'azienda e del rappresentante legale'
              : 'Inserisci i dati del titolare della fornitura'}
          </p>
        </div>
      </div>

      {/* Business: Dati Azienda */}
      {isBusiness && (
        <>
          <div className={styles.divider}>
            <span>Dati Azienda</span>
          </div>
          <div className={styles.formGrid}>
            <div className={styles.fullWidth}>
              <Input
                label="Ragione Sociale"
                icon="building"
                required
                uppercase
                error={errors.ragione_sociale?.message}
                {...register('ragione_sociale', {
                  required: 'Ragione sociale obbligatoria'
                })}
              />
            </div>
            <Input
              label="Partita IVA"
              icon="upc-scan"
              required
              maxLength={11}
              placeholder="12345678901"
              error={errors.partita_iva?.message}
              {...register('partita_iva', {
                required: 'Partita IVA obbligatoria',
                pattern: {
                  value: /^[0-9]{11}$/,
                  message: 'Partita IVA non valida (11 cifre)'
                }
              })}
            />
            <Input
              label="Codice Fiscale Azienda"
              icon="card-text"
              uppercase
              maxLength={16}
              error={errors.codice_fiscale_azienda?.message}
              hint="Se diverso dalla P.IVA"
              {...register('codice_fiscale_azienda')}
            />
            <Input
              label="Codice SDI"
              icon="hdd-network"
              maxLength={7}
              uppercase
              placeholder="0000000"
              error={errors.codice_sdi?.message}
              {...register('codice_sdi')}
            />
            <Input
              label="PEC Azienda"
              icon="envelope-at"
              type="email"
              placeholder="azienda@pec.it"
              error={errors.pec_azienda?.message}
              {...register('pec_azienda', {
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'PEC non valida'
                }
              })}
            />
          </div>

          {/* Sede Legale */}
          <div className={styles.divider}>
            <span>Sede Legale</span>
          </div>
          <div className={styles.formGrid}>
            <div className={styles.fullWidth}>
              <Input
                label="Indirizzo Sede Legale"
                icon="geo-alt"
                required
                uppercase
                placeholder="Via Roma 1/A"
                hint="Inserisci indirizzo e numero civico nello stesso campo (es. VIA ROMA 1/A)"
                error={errors.sede_legale_indirizzo?.message}
                {...register('sede_legale_indirizzo', {
                  required: 'Indirizzo sede legale obbligatorio'
                })}
              />
            </div>
            <Input
              label="CAP"
              icon="mailbox"
              required
              maxLength={5}
              placeholder="00100"
              error={errors.sede_legale_cap?.message}
              {...register('sede_legale_cap', {
                required: 'CAP obbligatorio',
                pattern: { value: /^[0-9]{5}$/, message: 'CAP non valido' }
              })}
            />
            <Controller
              control={control}
              name="sede_legale_citta"
              rules={{ required: 'Città obbligatoria' }}
              render={({ field }) => (
                <ComuneAutocomplete
                  label="Città"
                  name={field.name}
                  required
                  icon="building"
                  value={field.value || ''}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase()
                    field.onChange(v)
                    sedeLegaleCittaConfirmedRef.current = false
                    const last = ultimoComuneSedeLegaleRef.current
                    if (!last || v.trim().toUpperCase() !== last.comune.trim().toUpperCase()) {
                      ultimoComuneSedeLegaleRef.current = null
                      setCoperturaWarningSedeLegale(null)
                    }
                  }}
                  onBlur={field.onBlur}
                  onSelect={handleSelezioneComuneSedeLegale}
                  error={errors.sede_legale_citta?.message}
                  hint="Seleziona il comune dal menu: la lista proviene dalla tabella ufficiale Heracomm"
                />
              )}
            />
            <Input
              label="Provincia"
              icon="pin-map"
              required
              uppercase
              maxLength={2}
              placeholder="RM"
              error={errors.sede_legale_provincia?.message}
              {...register('sede_legale_provincia', {
                required: 'Provincia obbligatoria',
                maxLength: { value: 2, message: 'Usa la sigla (es. RM)' }
              })}
            />

            {coperturaWarningSedeLegale ? (
              <div className={styles.coperturaComuneWarning} role="status">
                <i className="bi bi-exclamation-triangle-fill" aria-hidden />
                <p>{coperturaWarningSedeLegale}</p>
              </div>
            ) : null}
          </div>

          {/* Grande Impresa rimossa dalla UI: il campo `grande_impresa` resta
              nel modello dati (default false) per compatibilita' a valle. */}

          <div className={styles.formGrid}>
            <Input
              label="Agevolazione IVA"
              icon="percent"
              placeholder="Es. IVA agevolata 10%"
              error={errors.agevolazione_iva?.message}
              {...register('agevolazione_iva')}
            />
            <Input
              label="Agevolazione Accise"
              icon="percent"
              placeholder="Es. Accise ridotte"
              error={errors.agevolazione_accise?.message}
              {...register('agevolazione_accise')}
            />
          </div>

          {/* Rappresentante Legale */}
          <div className={styles.divider}>
            <span>Rappresentante Legale</span>
          </div>
          <div className={styles.formGrid}>
            <Input
              label="Nome Rappresentante"
              icon="person"
              required
              uppercase
              error={errors.rappresentante_legale_nome?.message}
              {...register('rappresentante_legale_nome', {
                required: 'Nome rappresentante obbligatorio',
                minLength: { value: 2, message: 'Nome troppo corto' }
              })}
            />
            <Input
              label="Cognome Rappresentante"
              icon="person"
              required
              uppercase
              error={errors.rappresentante_legale_cognome?.message}
              {...register('rappresentante_legale_cognome', {
                required: 'Cognome rappresentante obbligatorio',
                minLength: { value: 2, message: 'Cognome troppo corto' }
              })}
            />
            <div className={styles.fullWidth}>
              <Input
                label="Codice Fiscale Rappresentante"
                icon="card-text"
                required
                uppercase
                maxLength={16}
                placeholder="RSSMRA85M01H501Z"
                error={errors.rappresentante_legale_cf?.message}
                {...register('rappresentante_legale_cf', {
                  required: 'Codice fiscale rappresentante obbligatorio',
                  pattern: {
                    value: /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/i,
                    message: 'Codice fiscale non valido'
                  }
                })}
              />
            </div>
          </div>

          {/* Referente in Azienda */}
          <div className={styles.divider}>
            <span>Referente in Azienda</span>
          </div>
          <div className={styles.formGrid}>
            <Input
              label="Nome e Cognome Referente"
              icon="person-badge"
              placeholder="Mario Rossi"
              hint="Persona di riferimento per comunicazioni operative"
              error={errors.referente_azienda_nome?.message}
              {...register('referente_azienda_nome')}
            />
            <Input
              label="Telefono Referente"
              icon="telephone"
              type="tel"
              inputMode="numeric"
              placeholder="340 1234567"
              hint="Solo cifre, eventualmente con prefisso +39"
              error={errors.referente_azienda_telefono?.message}
              {...register('referente_azienda_telefono', {
                pattern: {
                  value: /^(\+39\s?)?[0-9\s]{9,15}$/,
                  message: 'Numero di telefono non valido (solo cifre)'
                },
                onChange: (e) => {
                  const v = e.target.value as string;
                  const cleaned = v.replace(/[^0-9+\s]/g, '');
                  if (cleaned !== v) e.target.value = cleaned;
                }
              })}
            />
          </div>
        </>
      )}

      {/* Personal data - domestico only: nome/cognome/CF del titolare */}
      {!isBusiness && (
        <div className={styles.formGrid}>
          <Input
            label="Nome"
            icon="person"
            required
            uppercase
            autoComplete="given-name"
            error={errors.nome?.message}
            {...register('nome', { 
              required: 'Nome obbligatorio',
              minLength: { value: 2, message: 'Nome troppo corto' }
            })}
          />
          
          <Input
            label="Cognome"
            icon="person"
            required
            uppercase
            autoComplete="family-name"
            error={errors.cognome?.message}
            {...register('cognome', { 
              required: 'Cognome obbligatorio',
              minLength: { value: 2, message: 'Cognome troppo corto' }
            })}
          />
          
          <div className={styles.fullWidth}>
            <Input
              label="Codice Fiscale"
              icon={isDecodingCF ? "arrow-repeat" : "card-text"}
              required
              uppercase
              maxLength={16}
              autoComplete="off"
              placeholder="RSSMRA85M01H501Z"
              // Quando il bypass e' attivo NON propaghiamo l'errore al campo,
              // cosi' l'avviso giallo (warningBanner) e' l'unico segnale
              // visibile per evitare di "raddoppiare" il messaggio rosso.
              // cfDecodingError ha la precedenza perche' spiega *cosa* non
              // torna (es. la data che il codice indica), mentre il messaggio
              // di validazione resta generico.
              error={cfBypass ? undefined : (cfDecodingError || errors.codice_fiscale?.message || undefined)}
              hint={
                isDecodingCF
                  ? "Estrazione dati in corso..."
                  : cfBypass
                    ? "Validazione disabilitata: stai procedendo nonostante l'avviso sul codice fiscale"
                    : (cfDecodingError ? undefined : "Inserendo il CF, data e luogo di nascita verranno compilati automaticamente")
              }
              {...register('codice_fiscale', {
                required: 'Codice fiscale obbligatorio',
                // Il pattern e' attivo solo se l'operatore non ha richiesto
                // esplicitamente di procedere comunque con un CF non valido.
                pattern: cfBypass
                  ? undefined
                  : {
                      value: /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/i,
                      message: 'Codice fiscale non valido'
                    },
                // Il pattern verifica solo la forma e lascia passare refusi come
                // il giorno 39, che poi fanno fallire il salvataggio. Il
                // carattere di controllo li intercetta subito, qui in pagina.
                validate: (value) => {
                  if (cfBypass || !value) return true
                  if (!hasFormatoCodiceFiscaleValido(value)) return true
                  if (!isCodiceFiscaleValido(value)) {
                    return 'Codice fiscale errato: non supera il controllo di validità'
                  }
                  return true
                },
                onChange: () => {
                  setCfDecodingError(null)
                  // Se l'operatore sta riscrivendo il CF, riabilitiamo
                  // la validazione automatica: i nuovi dati vanno
                  // ricontrollati senza dover ripremere "Procedi comunque".
                  if (cfBypass) setCfBypass(false)
                }
              })}
            />

            {/* Banner "Procedi comunque" - solo domestico, mostrato solo
                quando c'e' un effettivo errore di validazione sul CF
                (pattern non rispettato o decodifica non riuscita) e l'utente
                non ha ancora richiesto il bypass. */}
            {!cfBypass && (errors.codice_fiscale || cfDecodingError) && (
              <div className={styles.warningBanner} role="status">
                <i className="bi bi-exclamation-triangle" />
                <div style={{ flex: 1 }}>
                  <strong>Codice fiscale da verificare</strong>
                  <p>
                    Il codice non supera i controlli automatici: quasi sempre si
                    tratta di un refuso su una cifra della data di nascita o sul
                    comune. Confrontalo con la tessera sanitaria.
                  </p>
                  <p>
                    Se il codice è corretto così com'è puoi proseguire, ma
                    ricontrolla data e luogo di nascita: non vengono compilati
                    automaticamente quando il codice non è riconosciuto.
                  </p>
                  <button
                    type="button"
                    className={`${styles.warningButton} btn-outline-warning`}
                    onClick={() => {
                      setCfBypass(true)
                      clearErrors('codice_fiscale')
                    }}
                    style={{
                      marginTop: '0.75rem',
                      padding: '0.4rem 0.9rem',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      border: '1px solid var(--color-warning-300)',
                      borderRadius: 'var(--radius-md)',
                      background: 'white',
                      color: 'var(--color-warning-800)',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                    }}
                  >
                    <i className="bi bi-arrow-right-circle" />
                    Procedi comunque
                  </button>
                </div>
              </div>
            )}

            {/* Stato bypass attivo: mostriamo una piccola conferma con
                possibilita' di ripristinare la validazione. */}
            {cfBypass && (
              <div className={styles.warningBanner} role="status" style={{ marginTop: '0.5rem' }}>
                <i className="bi bi-info-circle" />
                <div style={{ flex: 1 }}>
                  <strong>Stai procedendo con un codice fiscale non standard</strong>
                  <p>
                    La validazione automatica del CF e' stata disattivata per
                    questa pratica. Verifica i dati anagrafici prima di
                    inviare al cliente.
                  </p>
                  <button
                    type="button"
                    onClick={() => setCfBypass(false)}
                    style={{
                      marginTop: '0.75rem',
                      padding: '0.4rem 0.9rem',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      border: '1px solid var(--color-warning-300)',
                      borderRadius: 'var(--radius-md)',
                      background: 'transparent',
                      color: 'var(--color-warning-800)',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                    }}
                  >
                    <i className="bi bi-arrow-counterclockwise" />
                    Ripristina validazione
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Birth data + contact fields.
          - Domestico: dati anagrafici dell'intestatario (titolare fornitura).
          - Business: dati anagrafici del rappresentante legale (il titolare
            fisico della societa'). Sono OBBLIGATORI perche' confluiscono
            nella dichiarazione sostitutiva e nei moduli SEPA/contratto.
          Aggiungiamo un divider esplicito per il business cosi' l'operatore
          capisce che questi non sono dati del "referente operativo" ma del
          rappresentante legale. */}
      {isBusiness && (
        <div className={styles.divider}>
          <span>Dati Anagrafici Rappresentante Legale</span>
        </div>
      )}
      <div className={styles.formGrid}>
        <Input
          label={isBusiness ? 'Data di Nascita Rappresentante' : 'Data di Nascita'}
          icon="calendar-date"
          required
          placeholder="GG/MM/AAAA"
          hint={isBusiness ? 'Compilata automaticamente dal CF del rappresentante legale' : undefined}
          error={errors.data_nascita?.message}
          {...register('data_nascita', { 
            required: 'Data di nascita obbligatoria',
            pattern: {
              value: /^(\d{2}\/\d{2}\/\d{4}|\d{4}-\d{2}-\d{2})$/,
              message: 'Formato data non valido (GG/MM/AAAA)'
            },
            validate: (value) => {
              if (!value) return true
              // Il pattern ha già verificato la forma: se il parsing fallisce
              // qui, la data non esiste sul calendario (es. 39/06/2002).
              if (!parseDataIta(value)) {
                return 'Data inesistente: controlla giorno, mese e anno'
              }
              const eta = calcolaEta(value)
              if (eta === null) return true
              if (eta < 18) return 'L\'offerta è riservata ai maggiorenni (età minima 18 anni)'
              if (eta >= 80) return 'L\'offerta non è sottoscrivibile per l\'intestatario selezionato (età superiore a 80 anni)'
              return true
            }
          })}
        />
        
        <Input
          label={isBusiness ? 'Luogo di Nascita Rappresentante' : 'Luogo di Nascita'}
          icon="geo"
          required
          uppercase
          placeholder="Roma"
          hint={isBusiness ? 'Compilato automaticamente dal CF del rappresentante legale' : undefined}
          error={errors.luogo_nascita?.message}
          {...register('luogo_nascita', { 
            required: 'Luogo di nascita obbligatorio',
            minLength: { value: 2, message: 'Inserire il comune di nascita per esteso' }
          })}
        />
        
        <Input
          label={isBusiness ? 'Prov. Nascita Rappresentante' : 'Prov. Nascita'}
          icon="pin-map"
          required
          uppercase
          maxLength={2}
          placeholder="RM"
          error={errors.provincia_nascita?.message}
          {...register('provincia_nascita', { 
            required: 'Provincia di nascita obbligatoria',
            pattern: {
              value: /^[A-Za-z]{2}$/,
              message: 'Usa la sigla provincia (2 lettere, es. RM)'
            }
          })}
        />
      </div>

      <div className={styles.formGrid}>
        <Input
          label={isBusiness ? 'Telefono Rappresentante' : 'Telefono'}
          icon="telephone"
          type="tel"
          inputMode="numeric"
          required
          autoComplete="tel"
          placeholder="3331234567"
          hint="Solo cifre, eventualmente con prefisso +39"
          error={errors.telefono?.message}
          {...register('telefono', {
            required: 'Telefono obbligatorio',
            pattern: {
              value: /^(\+39\s?)?[0-9\s]{9,15}$/,
              message: 'Numero di telefono non valido (solo cifre)'
            },
            onChange: (e) => {
              const v = e.target.value as string
              const cleaned = v.replace(/[^0-9+\s]/g, '')
              if (cleaned !== v) e.target.value = cleaned
            }
          })}
        />
        
        <Input
          label={isBusiness ? 'Email Rappresentante' : 'Email'}
          icon="envelope"
          type="email"
          required
          autoComplete="email"
          placeholder="email@esempio.it"
          error={errors.email?.message}
          {...register('email', { 
            required: 'Email obbligatoria',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Email non valida'
            }
          })}
        />
        
        <Input
          label={isBusiness ? 'PEC Rappresentante' : 'PEC'}
          icon="envelope-at"
          type="email"
          autoComplete="email"
          placeholder="pec@pec.esempio.it"
          hint="Posta Elettronica Certificata (opzionale)"
          error={errors.pec?.message}
          {...register('pec', { 
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'PEC non valida'
            }
          })}
        />
      </div>

      {/* Banner Minorenne - Offerta non sottoscrivibile */}
      {isMinorenne && (
        <div className={styles.alertBanner}>
          <div className={styles.alertBannerIcon}>
            <i className="bi bi-exclamation-triangle-fill" />
          </div>
          <div className={styles.alertBannerContent}>
            <strong>Offerta non sottoscrivibile</strong>
            <p>L'offerta è riservata ai maggiorenni. L'intestatario deve avere almeno 18 anni.</p>
          </div>
        </div>
      )}

      {/* Banner Over 80 - Offerta non sottoscrivibile */}
      {isOver80 && (
        <div className={styles.alertBanner}>
          <div className={styles.alertBannerIcon}>
            <i className="bi bi-exclamation-triangle-fill" />
          </div>
          <div className={styles.alertBannerContent}>
            <strong>Offerta non sottoscrivibile</strong>
            <p>L'offerta non e sottoscrivibile per l'intestatario selezionato (eta superiore a 80 anni).</p>
          </div>
        </div>
      )}

      {/* Indirizzo di Residenza (domestico) / Indirizzo Rappresentante (business) */}
      <div className={styles.divider}>
        <span>{isBusiness ? 'Indirizzo Rappresentante Legale' : 'Indirizzo di residenza'}</span>
      </div>

      <div className={styles.formGrid}>
        <div className={styles.fullWidth}>
          <Input
            label="Indirizzo"
            icon="geo-alt"
            required
            uppercase
            autoComplete="street-address"
            placeholder="Via Roma, 1"
            error={errors.indirizzo_residenza?.message}
            {...register('indirizzo_residenza', { 
              required: 'Indirizzo obbligatorio' 
            })}
          />
        </div>
        
        <Input
          label="CAP"
          icon="mailbox"
          required
          maxLength={5}
          autoComplete="postal-code"
          placeholder="00100"
          error={errors.cap_residenza?.message}
          {...register('cap_residenza', { 
            required: 'CAP obbligatorio',
            pattern: {
              value: /^[0-9]{5}$/,
              message: 'CAP non valido'
            }
          })}
        />
        
        <Controller
          control={control}
          name="citta_residenza"
          rules={{ required: 'Città obbligatoria' }}
          render={({ field }) => (
            <ComuneAutocomplete
              label="Città"
              name={field.name}
              required
              icon="building"
              value={field.value || ''}
              onChange={(e) => {
                const v = e.target.value.toUpperCase()
                field.onChange(v)
                cittaResidenzaConfirmedRef.current = false
                const last = ultimoComuneResidenzaRef.current
                if (!last || v.trim().toUpperCase() !== last.comune.trim().toUpperCase()) {
                  ultimoComuneResidenzaRef.current = null
                  setCoperturaWarningResidenza(null)
                }
              }}
              onBlur={field.onBlur}
              onSelect={handleSelezioneComuneResidenza}
              error={errors.citta_residenza?.message}
              hint="Seleziona il comune dal menu: la lista proviene dalla tabella ufficiale Heracomm"
            />
          )}
        />

        <Input
          label="Provincia"
          icon="pin-map"
          required
          uppercase
          maxLength={2}
          autoComplete="address-level1"
          placeholder="RM"
          error={errors.provincia_residenza?.message}
          {...register('provincia_residenza', { 
            required: 'Provincia obbligatoria',
            maxLength: { value: 2, message: 'Usa la sigla (es. RM)' }
          })}
        />

        {coperturaWarningResidenza ? (
          <div className={styles.coperturaComuneWarning} role="status">
            <i className="bi bi-exclamation-triangle-fill" aria-hidden />
            <p>{coperturaWarningResidenza}</p>
          </div>
        ) : null}
      </div>

      {/* Checkbox stesso indirizzo */}
      <div className={styles.checkboxContainer}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            className={styles.checkbox}
            {...register('stesso_indirizzo')}
          />
          <span className={styles.checkboxCustom}>
            <i className="bi bi-check" />
          </span>
          <span className={styles.checkboxText}>
            <strong>
              {isBusiness
                ? 'L\'indirizzo di fornitura coincide con la sede legale'
                : 'L\'indirizzo di fornitura coincide con l\'indirizzo di residenza'}
            </strong>
            <small>Deseleziona se la fornitura è presso un indirizzo diverso</small>
          </span>
        </label>
      </div>

      {/* Indirizzo di Fornitura - visibile solo se diverso */}
      {!stessoIndirizzo && (
        <>
          <div className={styles.divider}>
            <span>Indirizzo di fornitura</span>
          </div>

          <div className={styles.formGrid}>
            <div className={styles.fullWidth}>
              <Input
                label="Indirizzo"
                icon="geo-alt"
                required
                uppercase
                autoComplete="street-address"
                placeholder="Via Roma, 1"
                error={errors.indirizzo_fornitura?.message}
                {...register('indirizzo_fornitura', { 
                  required: !stessoIndirizzo ? 'Indirizzo fornitura obbligatorio' : false
                })}
              />
            </div>
            
            <Input
              label="CAP"
              icon="mailbox"
              required
              maxLength={5}
              autoComplete="postal-code"
              placeholder="00100"
              error={errors.cap_fornitura?.message}
              {...register('cap_fornitura', { 
                required: !stessoIndirizzo ? 'CAP fornitura obbligatorio' : false,
                pattern: {
                  value: /^[0-9]{5}$/,
                  message: 'CAP non valido'
                }
              })}
            />
            
            <Controller
              control={control}
              name="citta_fornitura"
              rules={{
                required: !stessoIndirizzo ? 'Città fornitura obbligatoria' : false,
              }}
              render={({ field }) => (
                <ComuneAutocomplete
                  label="Città"
                  name={field.name}
                  required
                  icon="building"
                  value={field.value || ''}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase()
                    field.onChange(v)
                    cittaFornituraConfirmedRef.current = false
                    const last = ultimoComuneFornituraRef.current
                    if (!last || v.trim().toUpperCase() !== last.comune.trim().toUpperCase()) {
                      ultimoComuneFornituraRef.current = null
                      setCoperturaWarningFornitura(null)
                    }
                  }}
                  onBlur={field.onBlur}
                  onSelect={handleSelezioneComuneFornitura}
                  error={errors.citta_fornitura?.message}
                  hint="Seleziona il comune dal menu: la lista proviene dalla tabella ufficiale Heracomm"
                />
              )}
            />

            <Input
              label="Provincia"
              icon="pin-map"
              required
              uppercase
              maxLength={2}
              autoComplete="address-level1"
              placeholder="RM"
              error={errors.provincia_fornitura?.message}
              {...register('provincia_fornitura', { 
                required: !stessoIndirizzo ? 'Provincia fornitura obbligatoria' : false,
                maxLength: { value: 2, message: 'Usa la sigla (es. RM)' }
              })}
            />

            {coperturaWarningFornitura ? (
              <div className={styles.coperturaComuneWarning} role="status">
                <i className="bi bi-exclamation-triangle-fill" aria-hidden />
                <p>{coperturaWarningFornitura}</p>
              </div>
            ) : null}
          </div>
        </>
      )}

      <div className={styles.actions}>
        <Button
          type="button"
          variant="secondary"
          icon="arrow-left"
          onClick={prevStep}
        >
          Indietro
        </Button>
        
        <Button
          type="submit"
          icon="arrow-right"
          iconPosition="right"
          loading={isCheckingCopertura}
          disabled={isCheckingCopertura || isOver80 || isMinorenne}
        >
          {isCheckingCopertura ? 'Verifica copertura...' : 'Continua'}
        </Button>
      </div>

      {/* Modal Zona Non Coperta */}
      <Modal
        isOpen={showZonaNonCoperta}
        onClose={() => setShowZonaNonCoperta(false)}
        size="sm"
        showClose={false}
      >
        <div className={styles.zonaNonCopertaContent}>
          <div className={styles.zonaNonCopertaIconWrapper}>
            <div className={styles.zonaNonCopertaIconBg} />
            <div className={styles.zonaNonCopertaIcon}>
              <i className="bi bi-geo-alt-fill" />
            </div>
          </div>
          
          <div className={styles.zonaNonCopertaTextContent}>
            <h3 className={styles.zonaNonCopertaTitle}>
              Zona Non Coperta
            </h3>
            <p className={styles.zonaNonCopertaMessage}>
              L'offerta attualmente non &egrave; disponibile per il punto di fornitura indicato.
            </p>
            
            <div className={styles.zonaNonCopertaDetail}>
              <div className={styles.zonaNonCopertaDetailRow}>
                <i className="bi bi-lightning-fill" />
                <span>Luce</span>
                <span className={styles.zonaNonCopertaStatusNo}>Non attivabile</span>
              </div>
              <div className={styles.zonaNonCopertaDetailRow}>
                <i className="bi bi-fire" />
                <span>Gas</span>
                <span className={styles.zonaNonCopertaStatusNo}>Non attivabile</span>
              </div>
            </div>

            <p className={styles.zonaNonCopertaSubtext}>
              {coperturaMessage || 'Il servizio non \u00E8 ancora attivo nella zona selezionata. Puoi riprovare con un altro indirizzo di fornitura.'}
            </p>
          </div>

          <div className={styles.zonaNonCopertaActions}>
            <Button
              type="button"
              variant="primary"
              icon="arrow-counterclockwise"
              onClick={handleRicomincia}
              fullWidth
            >
              Ricomincia
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowZonaNonCoperta(false)}
              fullWidth
            >
              Modifica Dati Inseriti
            </Button>
          </div>
        </div>
      </Modal>
    </form>
  )
}
